import { useEffect, useState } from "react";
import type { GalleryImage } from "@/components/feature/GalleryGrid";
import { getVillaGalleryFiles } from "@/lib/villaApi";
import { useGalleryCaptions } from "@/hooks/useGalleryCaptions";
import { villaGalleryImages } from "@/pages/villas/gallery-data";

// Beschriftungs-Reserve aus dem Code (falls für ein Bild noch kein Text
// im Backstage hinterlegt wurde). Die echten Texte kommen aus der
// Datenbank und haben immer Vorrang.
const fallbackDe: Record<string, string> = villaGalleryImages.reduce(
  (acc, image) => {
    acc[image.key] = image.caption;
    return acc;
  },
  {} as Record<string, string>
);

const fallbackEn: Record<string, string> = villaGalleryImages.reduce(
  (acc, image) => {
    acc[image.key] = image.captionEn ?? image.caption;
    return acc;
  },
  {} as Record<string, string>
);

/**
 * Baut die Villen-Übersichts-Galerie direkt aus den echten Dateien im
 * Supabase-Ordner "villas/villa-gallery" auf – jede Datei genau einmal,
 * in Dateinamen-Reihenfolge. Die Beschriftungen werden anschließend mit
 * den im Backstage gepflegten Texten (Datenbank) überschrieben.
 *
 * Solange die Dateiliste lädt (oder falls sie nicht erreichbar ist),
 * greift automatisch die feste Liste aus dem Code.
 */
export function useVillaGalleryImages(): GalleryImage[] {
  const [files, setFiles] = useState<GalleryImage[] | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data, error } = await getVillaGalleryFiles();
        if (!active || error || !data || data.length === 0) return;
        const mapped: GalleryImage[] = data.map((file) => ({
          key: file.name,
          thumb: file.url,
          full: file.url,
          caption: fallbackDe[file.name] ?? file.name,
          captionEn: fallbackEn[file.name],
        }));
        setFiles(mapped);
      } catch {
        // Bei Fehlern bleibt die feste Liste aktiv.
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  return useGalleryCaptions("villas", files ?? villaGalleryImages);
}